import { Link } from "react-router-dom";
import { Loader2, ArrowRight, ChevronRight } from "lucide-react";
import { QICard } from "@/components/site/QICard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

export const Crumbs = ({ items }) => (
  <nav className="flex items-center gap-1.5 text-xs text-white/45 flex-wrap" data-testid="local-breadcrumb">
    {items.map(([label, to], i) => (
      <span key={label} className="inline-flex items-center gap-1.5">
        {i > 0 && <ChevronRight size={12} />}
        {to ? <Link to={to} className="hover:text-white">{label}</Link> : <span className="text-white/70">{label}</span>}
      </span>
    ))}
  </nav>
);

export const FaqSection = ({ heading, items }) => (
  <section className="mt-20 grid lg:grid-cols-12 gap-10" data-testid="local-faq">
    <div className="lg:col-span-4">
      <span className="eyebrow">1031 basics</span>
      <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-4">{heading}</h2>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-8 glass-card px-6">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`} className="border-white/10 last:border-0">
          <AccordionTrigger className="text-left font-display text-base font-bold hover:no-underline" data-testid={`local-faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-white/60 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const ChipLinks = ({ title, links, all, testid }) => (
  <section className="mt-20" data-testid={testid}>
    <span className="eyebrow">{title}</span>
    <div className="flex flex-wrap gap-2 mt-5">
      {links.map(([label, to]) => (
        <Link key={to} to={to} data-testid={`${testid}-${to.split("/").pop()}`}
          className="text-sm bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-white/70 hover:border-qi-pink/50 hover:text-white transition-colors">{label}</Link>
      ))}
      {all && <Link to={all[1]} className="text-sm rounded-full px-4 py-2 text-qi-pink font-bold" data-testid={`${testid}-all`}>{all[0]} →</Link>}
    </div>
  </section>
);

export const QIResults = ({ qis, place }) => {
  if (qis === null) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>;
  return (
    <>
      <p className="font-mono text-xs text-white/40 mb-4" data-testid="local-count">{qis.length} intermediar{qis.length === 1 ? "y" : "ies"} serving {place}</p>
      {qis.length === 0 ? (
        <div className="glass-card p-10 text-center" data-testid="local-empty">
          <p className="font-display text-xl font-bold">No reviewed QIs listed for {place} yet</p>
          <p className="text-white/50 text-sm mt-2">Browse intermediaries that serve other states or nationwide, or check back soon.</p>
          <Link to="/directory" className="btn-ghost mt-6" data-testid="local-browse-all-button">Browse all QIs</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{qis.map((qi) => <QICard key={qi.profile_id} qi={qi} />)}</div>
      )}
    </>
  );
};

export const ListCta = ({ place }) => (
  <div className="mt-14 glass-card p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
    <div>
      <p className="font-display text-xl font-bold">Are you a QI serving {place}?</p>
      <p className="text-sm text-white/50 mt-1">Get listed on this page. The first 30 intermediaries list free.</p>
    </div>
    <Link to="/login?mode=register" className="btn-pink" data-testid="local-list-qi-button">List your firm <ArrowRight size={16} /></Link>
  </div>
);
